import { memo, ReactNode } from 'react'
import ChevronRight from '@mui/icons-material/ChevronRight'
import { Typography, Theme } from '@mui/material'
import { useClientDetails } from 'stores/utils/hooks/useClientDetails'
import * as CSSTypes from 'csstype'
import useEmotionStyles from 'hooks/useEmotionStyles'

const styles = (theme: Theme) => ({
  container: {
    padding: '21px',
    width: '272px',

    [theme.breakpoints.down('sm')]: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column' as CSSTypes.Property.FlexDirection,
      justifyContent: 'space-between'
    }
  },
  styledTitle: {
    paddingBottom: theme.spacing(2)
  },
  content: {
    paddingBottom: theme.spacing(2)
  },
  logo: {
    maxHeight: '85px',
    maxWidth: '67px',
    paddingBottom: theme.spacing(2)
  },
  link: {
    textDecoration: 'none',
    color: theme.palette.primary.main
  },
  icon: {
    verticalAlign: 'middle',
    display: 'inline-block',
    fontSize: '1.2rem'
  },
  button: {
    color: theme.palette.primary.main,
    background: 'none',
    padding: 0,
    cursor: 'pointer',
    justifyContent: 'start',
    '&:hover': {
      backgroundColor: 'transparent'
    }
  },
  buttonText: {
    color: theme.palette.primary.main,
    fontWeight: 700
  }
})

interface CardProps {
  logoName: string
  title: string
  body?: string
  linkTo?: string
}

const Card = ({ logoName, title, body, linkTo }: CardProps) => {
  const { container, logo, link, icon, buttonText, styledTitle, content } =
    useEmotionStyles(styles)

  const clientDetails = useClientDetails()
  const cardAction = (innerText: ReactNode) => {
    let component = <p />
    if (linkTo) {
      component = (
        <a className={link} href={linkTo} target="_blank" rel="noreferrer">
          {innerText}
        </a>
      )
    }
    return component
  }

  return (
    <div className={container}>
      {clientDetails?.logos?.[logoName] && (
        <img
          className={logo}
          src={clientDetails.logos[logoName]}
          alt={`logo-${title.toLowerCase().replace('', '-')}`}
          loading="lazy"
        />
      )}
      <Typography variant="h4" className={styledTitle}>
        {title}
      </Typography>
      <Typography className={content} variant="body2">
        {body}
      </Typography>
      {cardAction(
        <Typography variant="body2" className={buttonText}>
          Weiterlesen <ChevronRight className={icon} />
        </Typography>
      )}
    </div>
  )
}

export default memo(Card)
